import {
  Add,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  Remove,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  Fade,
  IconButton,
  Slider,
  Stack,
  styled,
  TextField,
  Typography,
} from "@mui/material";
import nuclides, { getNuclide } from "core/services/nuclideService";
import React from "react";
import AtomicModel from "./AtomicModel";
import NuclideInfo from "./NuclideInfo";

const atomicModelDefautSizeSetting = {
  width: {
    ratio: 0.3,
    max: 210,
    min: 210,
  },
  height: {
    ratio: 0.25,
    max: 210,
    min: 210,
  },
};

const NuclideControlCard = ({
  selectedNuclide,
  setSelectedNuclide,
  setOpenDrawer,
  isDesktop,
}) => {
  const handleSetInput = (e) => {
    const { name, value } = e.target;

    setSelectedNuclide((sn) => {
      const newPoint = {
        proton: sn?.proton,
        neutron: sn?.neutron,
        [name]: parseInt(value),
      };
      return getNuclide(newPoint) || newPoint;
    });
  };

  const handleAddOrMinus = (name, dir) => () => {
    setSelectedNuclide((sn) => {
      const newPoint = {
        proton: sn?.proton,
        neutron: sn?.neutron,
        [name]: sn?.[name] + dir,
      };
      return getNuclide(newPoint) || newPoint;
    });
  };

  return (
    <StyledCardContainer square>
      <Stack direction="row" spacing={1} justifyContent="space-evenly">
        <SliderInput
          name="proton"
          onChange={handleSetInput}
          handleAddOrMinus={handleAddOrMinus}
          max={nuclides.highest.proton}
          value={selectedNuclide?.proton}
          label="Proton"
          color="secondary"
        />
        <Box minHeight="410px" width="100%" height="100%" mx="1em">
          {selectedNuclide?.name ? (
            <Fade in>
              <Stack alignItems="center" justifyContent="space-between">
                <Typography textAlign="center" variant="h5" height="62px">
                  <b
                    className={
                      selectedNuclide.primary_decay === "Stable"
                        ? "stable-element"
                        : selectedNuclide.primary_decay === "Unknown"
                        ? "unknown-element"
                        : "radioactive-element"
                    }
                  >
                    {selectedNuclide.name}
                  </b>
                </Typography>
                <AtomicModel
                  selectedNuclide={selectedNuclide}
                  defaultSizeSetting={atomicModelDefautSizeSetting}
                />
                <NuclideInfo selectedNuclide={selectedNuclide} />
                {!isDesktop && (
                  <Box my="5px">
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={() => {
                        setOpenDrawer(true);
                      }}
                    >
                      Show Usage
                    </Button>
                  </Box>
                )}
              </Stack>
            </Fade>
          ) : (
            <Stack
              minHeight="400px"
              justifyContent="center"
              alignItems="center"
            >
              <Typography variant="h5" textAlign="center" justifySelf="center">
                Isotope does not exist
              </Typography>
            </Stack>
          )}
        </Box>
        <SliderInput
          name="neutron"
          onChange={handleSetInput}
          handleAddOrMinus={handleAddOrMinus}
          max={nuclides.highest.neutron}
          value={selectedNuclide?.neutron}
          label="Neutron"
          color="primary"
        />
      </Stack>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="center"
        px="20px"
        pb="10px"
        spacing={2}
      >
        <IconButton
          sx={purpleColorSX}
          onClick={() => {
            setSelectedNuclide(nuclides.flatten[selectedNuclide?.index - 1]);
          }}
          disabled={selectedNuclide?.index <= 0}
        >
          <KeyboardArrowLeft />
        </IconButton>
        <Slider
          size="small"
          sx={purpleColorPaddingSX}
          valueLabelDisplay="auto"
          value={selectedNuclide?.index || 0}
          onChange={(e) => {
            setSelectedNuclide(nuclides.flatten[Number(e.target.value)]);
          }}
          min={0}
          max={nuclides.flatten.length - 1}
        />
        <IconButton
          sx={purpleColorSX}
          disabled={selectedNuclide?.index >= nuclides.flatten.length - 1}
          onClick={() => {
            setSelectedNuclide(nuclides.flatten[selectedNuclide?.index + 1]);
          }}
        >
          <KeyboardArrowRight />
        </IconButton>
      </Stack>
    </StyledCardContainer>
  );
};

const purpleColorPaddingSX = { color: "purple", padding: "0 !important" };
const purpleColorSX = { color: "purple", padding: "0 !important" };
const sliderSizeSX = { width: "45px" };
const inputProps = {
  inputMode: "numeric",
  style: {
    fontSize: "20px",
    fontWeight: "bold",
    textAlign: "center",
  },
};

const onFocus = (event) => {
  event.target.select();
};

const SliderInput = ({
  value,
  onChange,
  name,
  label,
  max,
  color,
  handleAddOrMinus,
}) => {
  return (
    <Stack justifyContent="center" alignItems="center" width="80px">
      <TextField
        size="small"
        onFocus={onFocus}
        margin="dense"
        label={label}
        color={color}
        fullWidth
        inputProps={inputProps}
        value={value || ""}
        type="number"
        onChange={onChange}
        name={name}
      />

      <IconButton
        color={color}
        onClick={handleAddOrMinus(name, 1)}
        disabled={value === max}
      >
        <Add />
      </IconButton>
      <Slider
        sx={sliderSizeSX}
        name={name}
        valueLabelDisplay="auto"
        orientation="vertical"
        color={color}
        value={value || 0}
        onChange={onChange}
        min={0}
        max={max}
      />
      <IconButton
        color={color}
        disabled={value === 0}
        onClick={handleAddOrMinus(name, -1)}
      >
        <Remove />
      </IconButton>
    </Stack>
  );
};

const StyledCardContainer = styled(Card)({
  padding: "10px 10px 0px 10px !important",
  height: "calc(100% - 10px)",
});

export default NuclideControlCard;
